import { Button, Empty, Image, Modal, Typography, Input } from "antd";
import { Camera, FileText, Package, ArrowRight, Download, Eye, Search } from "lucide-react";
import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useGetMyFiles } from "~/services/file";
import { useGetMyInventories } from "~/services/inventory";
import { TFile, TImage } from "~/types";
import { SortOrder, CheckType } from "~/utils/constants";
import { RESPONSIVE_CLASSES, getResponsiveModalWidth } from "~/utils/responsive";
import { DocumentUploadModal } from "./document-upload-modal";
import { useNotification } from "~/hooks";
import { useGetFileMimetype } from "~/services/common";
import { useAuth } from "~/store";

const { Title, Text } = Typography;

interface DocumentPreviewProps {
  doc: TFile;
}

const DocumentPreview: React.FC<DocumentPreviewProps> = ({ doc }) => {
  const fileUrl = `${doc.filePath}`;
  const [canLoad, setCanLoad] = useState<boolean | null>(null);

  const { data: mimeType } = useGetFileMimetype(doc.filePath);

  const canLoadFile = async (url: string) => {
    try {
      const response = await fetch(url, { method: "GET" });
      return response.ok;
    } catch (error) {
      console.error("Error checking file:", error);
      return false;
    }
  };

  React.useEffect(() => {
    canLoadFile(fileUrl).then((result) => setCanLoad(result));
  }, [fileUrl]);

  if (canLoad === null) {
    return <Text>Loading...</Text>;
  }

  if (!canLoad) {
    return (
      <div className="text-center p-4">
        <Text>
          Unable to load the document. The file may not exist or the server is
          not running.
        </Text>
      </div>
    );
  }

  switch (mimeType) {
    case "image/jpeg":
    case "image/png":
      return (
        <div className="text-center">
          <img
            src={fileUrl}
            alt={doc.title}
            className="max-w-full max-h-[60vh] sm:max-h-[70vh] object-contain rounded"
            onError={() => setCanLoad(false)}
          />
        </div>
      );
    case "application/pdf":
      return (
        <div className="w-full">
          <embed
            src={fileUrl}
            type="application/pdf"
            className="w-full h-[50vh] sm:h-[60vh] lg:h-[70vh] rounded"
            onError={() => setCanLoad(false)}
          />
        </div>
      );
    case "application/msword":
    case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    case "text/csv":
      return (
        <div className="text-center p-4">
          <Text className={RESPONSIVE_CLASSES.bodyText}>
            This file type ({mimeType.split("/")[1].toUpperCase()}) cannot be
            previewed in the browser.
          </Text>
          <Button
            type="primary"
            href={fileUrl}
            download={doc.fileName || "document"}
            className={`${RESPONSIVE_CLASSES.buttonSize} mt-4`}
          >
            Download File
          </Button>
        </div>
      );
    default:
      return <Text className={RESPONSIVE_CLASSES.bodyText}>Unsupported file type</Text>;
  }
};

export const Documents: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selectedDoc, setSelectedDoc] = useState<TFile | null>(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [uploadVisible, setUploadVisible] = useState(false);
  const [documentType, setDocumentType] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const { error } = useNotification();

  // Get inventories with images to extract photos
  const {
    data: inventoriesData,
    isLoading: imagesLoading,
  } = useGetMyInventories({
    checkParentId: CheckType.CHECK,
    pageSize: 100,
  });

  // Get documents tagged to bookings (files with jobTicketId)
  // Fetch a larger set to support search functionality across all documents
  const {
    data: bookingFilesData,
    isLoading: bookingFilesLoading,
  } = useGetMyFiles({
    pageSize: 500, // Fetch up to 500 documents to support search across all documents
    sortByCreatedAt: SortOrder.DESC,
    clientId: user?.id,
  });

  // Filter to only show files that have a jobTicketId (tagged to bookings)
  const allBookingDocuments = useMemo(() => {
    if (!bookingFilesData?.items) return [];
    return bookingFilesData.items.filter((file) => file.jobTicket?.id);
  }, [bookingFilesData]);

  // Get total count of documents with job tickets from the API
  // Note: This is an approximation since we filter client-side
  const totalDocumentsAvailable = bookingFilesData?.meta?.totalItems || 0;
  const documentsFetched = bookingFilesData?.items?.length || 0;
  const hasMoreDocuments = totalDocumentsAvailable > documentsFetched;

  // Filter by search term
  const filteredBookingDocuments = useMemo(() => {
    if (!searchTerm.trim()) return allBookingDocuments;
    const search = searchTerm.toLowerCase();
    return allBookingDocuments.filter((doc) => {
      const title = doc.title?.toLowerCase() || "";
      const bookingRef = doc.jobTicket?.referenceNumber?.toLowerCase() || "";
      const fileName = doc.fileName?.toLowerCase() || "";
      return title.includes(search) || bookingRef.includes(search) || fileName.includes(search);
    });
  }, [allBookingDocuments, searchTerm]);

  // Paginate documents
  const paginatedBookingDocuments = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredBookingDocuments.slice(startIndex, endIndex);
  }, [filteredBookingDocuments, currentPage, pageSize]);

  const totalPages = Math.ceil(filteredBookingDocuments.length / pageSize);

  // Extract images from inventories
  const photosWithInventory = useMemo(() => {
    if (!inventoriesData?.items) return [];
    const imageMap = new Map<string, { image: TImage; inventory: any }>();
    
    inventoriesData.items.forEach((inventory) => {
      if (inventory.images && inventory.images.length > 0) {
        inventory.images.forEach((image) => {
          // Use image ID as key to avoid duplicates
          if (!imageMap.has(image.id)) {
            imageMap.set(image.id, { image, inventory });
          }
        });
      }
    });
    
    return Array.from(imageMap.values());
  }, [inventoriesData]);

  const handleCreateDocument = (type: string) => {
    setDocumentType(type);
    setUploadVisible(true);
  };

  const viewDocument = (doc: TFile) => {
    if (!doc.filePath) {
      error({
        message: "Cannot view document",
        description: "Document URL is missing.",
      });
      return;
    }
    setSelectedDoc(doc);
    setPreviewVisible(true);
  };

  const downloadDocument = async (doc: TFile) => {
    if (!doc.filePath) {
      error({
        message: "Cannot download document",
        description: "Document URL is missing.",
      });
      return;
    }
    try {
      const response = await fetch(doc.filePath, { method: "GET" });
      if (!response.ok) throw new Error("Failed to fetch file");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = doc.fileName || "document";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      error({
        message: "Failed to download file",
        description: err.message,
      });
    }
  };

  const handleClosePreview = () => {
    setPreviewVisible(false);
    setSelectedDoc(null);
  };

  const handleCloseUpload = () => {
    setUploadVisible(false);
    setDocumentType(null);
  };

  const handleUploadSuccess = () => {
    // Refetch will be handled by react-query
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  return (
    <div className={RESPONSIVE_CLASSES.container}>
      {/* Header - Neo Brutalist Style */}
      <div className={RESPONSIVE_CLASSES.marginBottom}>
        <div className="border-2 border-black inline-block px-4 py-2 mb-2">
          <Title level={2} className={`${RESPONSIVE_CLASSES.titleText} font-bold text-black !mb-0 uppercase tracking-wide`}>
            Documents
          </Title>
        </div>
        <p className={`${RESPONSIVE_CLASSES.bodyText} text-black font-medium`}>
          View photos tagged to inventory and documents tagged to bookings
        </p>
      </div>

      {/* Photos with Inventory Tags - Neo Brutalist Card */}
      <div className="border-2 border-black bg-[#fffff5] mb-6">
        <div className="border-b-2 border-black p-4 bg-black text-white">
          <div className="flex items-center gap-2">
            <Camera size={18} />
            <span className="font-bold text-sm uppercase tracking-wide">Photos Tagged to Inventory</span>
            <span className="ml-auto font-mono font-bold">{photosWithInventory.length}</span>
          </div>
        </div>
        <div className="p-4">
          {imagesLoading ? (
            <div className="p-6 text-center">
              <Text className="font-medium">Loading photos...</Text>
            </div>
          ) : photosWithInventory.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {photosWithInventory.map(({ image, inventory }) => (
                <div key={image.id} className="border-2 border-black bg-[#fffff5]">
                  <div className="aspect-square overflow-hidden bg-[#fffff5]">
                    <Image
                      src={image.url}
                      alt={image.name || "Photo"}
                      className="w-full h-full object-cover"
                      preview={{
                        mask: (
                          <div className="flex flex-col items-center justify-center">
                            <Camera size={16} />
                            <span className="text-xs mt-1 font-bold uppercase">Preview</span>
                          </div>
                        ),
                      }}
                    />
                  </div>
                  {inventory && (
                    <div className="p-2 border-t-2 border-black bg-[#fffff5]">
                      <div className="flex items-center gap-1 mb-1">
                        <Package size={12} className="text-black" />
                        <span className="font-bold text-xs uppercase tracking-wide text-black">Inventory:</span>
                      </div>
                      <div className="text-black font-medium text-xs">
                        {inventory.title || inventory.inventoryRef || "Untitled"}
                      </div>
                      {inventory.inventoryRef && (
                        <div className="text-gray-600 text-xs mt-1 font-mono">
                          Ref: {inventory.inventoryRef}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center border-2 border-dashed border-black">
              <Empty
                description={<span className="font-medium text-black">No photos tagged to inventory</span>}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            </div>
          )}
        </div>
      </div>

      {/* Documents Tagged to Bookings - Neo Brutalist Card */}
      <div className="border-2 border-black bg-[#fffff5] mb-6">
        <div className="border-b-2 border-black p-4 bg-black text-white">
          <div className="flex items-center gap-2">
            <FileText size={18} />
            <span className="font-bold text-sm uppercase tracking-wide">Documents Tagged to Bookings</span>
            <span className="ml-auto font-mono font-bold">{filteredBookingDocuments.length}</span>
          </div>
        </div>
        <div className="p-4">
          {/* Search Input - Neo Brutalist Style */}
          <div className="mb-4">
            <div className="relative">
              <Search 
                size={18} 
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-black z-10" 
              />
              <input
                type="text"
                placeholder="Search by document title, booking reference, or filename..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1); // Reset to first page on search
                }}
                className="w-full pl-10 pr-4 py-3 border-2 border-black bg-[#fffff5] text-black font-medium focus:outline-none focus:ring-0 focus:border-black"
              />
            </div>
          </div>

          {bookingFilesLoading ? (
            <div className="p-6 text-center">
              <Text className="font-medium">Loading documents...</Text>
            </div>
          ) : filteredBookingDocuments.length > 0 ? (
            <>
              <div className="space-y-2 mb-4">
                {paginatedBookingDocuments.map((doc) => (
                  <div
                    key={doc.id}
                    className="border-2 border-black bg-[#fffff5] p-4 hover:bg-black hover:text-white transition-all cursor-pointer"
                    onClick={() => viewDocument(doc)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 flex-1">
                        <div className="border-2 border-black bg-[#fffff5] p-2">
                          <FileText size={20} className="text-black" />
                        </div>
                        <div className="flex-1">
                          <div className="font-bold text-black hover:text-white">{doc.title || "Unnamed Document"}</div>
                          {doc.jobTicket && (
                            <div className="text-xs text-gray-600 hover:text-gray-300 mt-1 space-y-0.5">
                              <div className="font-mono">
                                Booking: {doc.jobTicket.referenceNumber || doc.jobTicket.id.slice(0, 8)}...
                              </div>
                              {doc.jobTicket.client && (
                                <div className="font-medium">
                                  Client: {doc.jobTicket.client.companyName || `${doc.jobTicket.client.firstName} ${doc.jobTicket.client.lastName}`.trim() || 'Unknown Client'}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-mono font-medium text-black hover:text-white">
                          {formatDate(doc.createdAt)}
                        </span>
                        <button
                          className="border-2 border-black bg-[#fffff5] text-black hover:bg-black hover:text-white px-3 py-1 font-bold text-xs uppercase transition-all"
                          onClick={(e) => {
                            e.stopPropagation();
                            viewDocument(doc);
                          }}
                        >
                          <Eye size={14} className="inline mr-1" />
                          View
                        </button>
                        <button
                          className="border-2 border-black bg-black text-white hover:bg-[#fffff5] hover:text-black px-3 py-1 font-bold text-xs uppercase transition-all"
                          onClick={(e) => {
                            e.stopPropagation();
                            downloadDocument(doc);
                          }}
                        >
                          <Download size={14} className="inline mr-1" />
                          Download
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination - Neo Brutalist Style */}
              {totalPages > 1 && (
                <div className="flex flex-col gap-3 border-t-2 border-black pt-4">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium text-black">
                      Showing {(currentPage - 1) * pageSize + 1} - {Math.min(currentPage * pageSize, filteredBookingDocuments.length)} of {filteredBookingDocuments.length}
                      {hasMoreDocuments && (
                        <span className="text-xs text-gray-600 ml-2">
                          (showing first {documentsFetched} of {totalDocumentsAvailable} total)
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        className={`border-2 border-black px-4 py-2 font-bold text-xs uppercase transition-all ${
                          currentPage === 1
                            ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                            : "bg-[#fffff5] text-black hover:bg-black hover:text-white"
                        }`}
                      >
                        Previous
                      </button>
                      <div className="flex items-center gap-1">
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          let pageNum;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }
                          return (
                            <button
                              key={pageNum}
                              onClick={() => setCurrentPage(pageNum)}
                              className={`border-2 border-black px-3 py-1 font-bold text-xs uppercase transition-all ${
                                currentPage === pageNum
                                  ? "bg-black text-white"
                                  : "bg-[#fffff5] text-black hover:bg-black hover:text-white"
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                      </div>
                      <button
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                        className={`border-2 border-black px-4 py-2 font-bold text-xs uppercase transition-all ${
                          currentPage === totalPages
                            ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                            : "bg-[#fffff5] text-black hover:bg-black hover:text-white"
                        }`}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                  {hasMoreDocuments && (
                    <div className="text-xs text-gray-600 italic">
                      Note: Search is limited to the first {documentsFetched} documents. For best results, use specific search terms.
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="p-8 text-center border-2 border-dashed border-black">
              <Empty
                description={
                  <span className="font-medium text-black">
                    {searchTerm ? "No documents found matching your search" : "No documents tagged to bookings"}
                  </span>
                }
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            </div>
          )}
        </div>
      </div>

      {/* Button to Profile Documents - Neo Brutalist Style */}
      <div className="border-2 border-black bg-[#fffff5]">
        <div className="p-4 flex items-center justify-between">
          <div>
            <div className="font-bold text-black uppercase tracking-wide mb-1">Profile Documents</div>
            <div className="text-sm text-gray-700 font-medium">
              View contracts and other profile documents
            </div>
          </div>
          <button
            className="border-2 border-black bg-black text-white hover:bg-white hover:text-black px-6 py-3 font-bold text-sm uppercase tracking-wide transition-all flex items-center gap-2"
            onClick={() => navigate("/profile?tab=documents")}
          >
            <span>Go to Profile</span>
            <ArrowRight size={16} />
          </button>
        </div>
      </div>

      <Modal
        title={selectedDoc?.title || "View Document"}
        open={previewVisible}
        onCancel={handleClosePreview}
        footer={[
          <Button
            key="download"
            type="primary"
            onClick={() => selectedDoc && downloadDocument(selectedDoc)}
            className={RESPONSIVE_CLASSES.buttonSize}
          >
            Download File
          </Button>,
          <Button 
            key="close" 
            onClick={handleClosePreview}
            className={RESPONSIVE_CLASSES.buttonSize}
          >
            Close
          </Button>,
        ]}
        width={getResponsiveModalWidth()}
        className="document-preview-modal"
      >
        {selectedDoc && <DocumentPreview doc={selectedDoc} />}
      </Modal>

      <DocumentUploadModal
        visible={uploadVisible}
        documentType={documentType}
        onClose={handleCloseUpload}
        onSuccess={handleUploadSuccess}
      />
    </div>
  );
};
